import { FC, useEffect, useState } from 'react';
import { KTIcon } from '../../../_myb/helpers';
import { useThemeMode } from '../../../_myb/partials';
import { getStatusBrief } from '../../api';
import { globalValue } from '../../modules/apps/AppContext';

const handleUpgradeClick = () => {
  window.dispatchEvent(new CustomEvent('subscription:upgrade-required', {
    detail: { message: 'AI Insights is available on Premium or higher. Upgrade your plan to unlock it.'.toTranslatedDisplay() },
  }));
};

const LockedAiStatusBriefCard: FC<{ variant?: 'default' | 'v3' }> = ({ variant = 'default' }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const overlayBg = isDark ? 'rgba(28,28,45,0.82)' : 'rgba(255,255,255,0.55)';
  const lockTextColor = isDark ? '#e0e0e0' : '#181c32';

  return (
    <div
      className={`card position-relative overflow-hidden${variant === 'v3' ? ' h-100 w-100' : ' mb-6'}`}
      style={{ borderLeft: '3px solid #009EF7', ...(variant === 'v3' ? { minHeight: V3_MIN_HEIGHT } : {}) }}
    >
      <div className='card-body py-6 px-6' style={{ filter: 'blur(4px)', userSelect: 'none' }} aria-hidden='true'>
        <div className='d-flex align-items-center gap-2 mb-3'>
          <span
            className='w-30px h-30px rounded-circle d-flex align-items-center justify-content-center'
            style={{ background: 'linear-gradient(135deg, #009EF7 0%, #6B7CF8 100%)' }}
          >
            <KTIcon iconName='message-text-2' className='text-white fs-6' />
          </span>
          <span className='fw-bold text-gray-800 fs-6'>{'AI Business Summary'.toTranslatedDisplay()}</span>
        </div>
        <p className='text-gray-700 fs-7 mb-3' style={{ lineHeight: '1.7' }}>
          {'Your business is in good shape this week with steady revenue and a stable cash position.'.toTranslatedDisplay()}
        </p>
        <div className='d-flex flex-wrap gap-6 fs-7 text-gray-700'>
          <div>
            <div className='fw-semibold text-success mb-1'>{'Going well'.toTranslatedDisplay()}</div>
            <div>{'Revenue grew steadily'.toTranslatedDisplay()}</div>
            <div>{'Margins stayed healthy'.toTranslatedDisplay()}</div>
          </div>
          <div>
            <div className='fw-semibold text-warning mb-1'>{'Areas to improve'.toTranslatedDisplay()}</div>
            <div>{'Follow up overdue invoices'.toTranslatedDisplay()}</div>
            <div>{'Review expense growth'.toTranslatedDisplay()}</div>
          </div>
        </div>
      </div>
      <div
        className='position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2'
        style={{ background: overlayBg }}
      >
        <i className='bi bi-lock-fill text-warning fs-2'></i>
        <span className='fw-bold fs-6' style={{ color: lockTextColor }}>{'AI Insights Locked'.toTranslatedDisplay()}</span>
        <button className='btn btn-sm btn-primary py-1 px-3' onClick={handleUpgradeClick}>
          {'Upgrade Now'.toTranslatedDisplay()}
        </button>
      </div>
    </div>
  );
};

type BriefUnavailableReason = 'insufficient_data' | 'data_unavailable';

type StatusBrief = {
  summary: string;
  positives: string[];
  improvements: string[];
};

// variant='v3': Turn-2 mock's layout for the same summary/positives/improvements data --
// a larger verdict sentence, then a "Going well" / "Do this next" split with numbered
// badges instead of the default's icon-bulleted list. Additive and opt-in so /dashboard
// (V2), which renders this same component unchanged, keeps today's exact appearance.
// Stale-while-revalidate: the brief comes from an LLM call and is slow, so the last one is
// cached per business and painted immediately while the fresh one loads in the background.
const briefCacheKey = () => `aiStatusBrief_${globalValue?.bizMaster?.bizId ?? ''}`;
const readCachedBrief = (): StatusBrief | null => {
  try {
    const raw = localStorage.getItem(briefCacheKey());
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Keeps both v3 dashboard cards the same minimum height so the row doesn't grow (and the
// page scrollbar doesn't appear/shift the layout width) when the brief finishes loading.
const V3_MIN_HEIGHT = 240;

const AiStatusBriefCard: FC<{ locked?: boolean; variant?: 'default' | 'v3' }> = ({ locked = false, variant = 'default' }) => {
  const [brief, setBrief] = useState<StatusBrief | null>(() => (locked ? null : readCachedBrief()));
  // Starts true (unless locked) so the card never renders a blank "not loading yet" frame.
  const [loading, setLoading] = useState(!locked);
  // Collapse-to-toggle only applies to the 'default' variant (V2 dashboard). 'v3'
  // (dashboard's two-column Needs Attention row) is always expanded and matches the
  // row height via h-100, so there's no collapse affordance to remove state for.
  const [collapsed, setCollapsed] = useState(false);
  const isCollapsible = variant === 'default';
  const effectiveCollapsed = isCollapsible && collapsed;
  const [initialized, setInitialized] = useState(false);
  const [unavailable, setUnavailable] = useState<{ reason: BriefUnavailableReason; invoiceCount?: number; requiredInvoiceCount?: number } | null>(null);

  const fetchBrief = async () => {
    setLoading(true);
    try {
      const result = await getStatusBrief();
      const fresh: StatusBrief | null = result?.summary ? {
        summary: result.summary,
        positives: result.positives ?? [],
        improvements: result.improvements ?? [],
      } : null;
      setBrief(fresh);
      try {
        if (fresh) localStorage.setItem(briefCacheKey(), JSON.stringify(fresh));
        else localStorage.removeItem(briefCacheKey());
      } catch { /* quota / private mode */ }
      setUnavailable(result?.summary ? null : result?.reason ? {
        reason: result.reason,
        invoiceCount: result.invoiceCount,
        requiredInvoiceCount: result.requiredInvoiceCount,
      } : null);
    } catch (err) {
      console.error('[AiStatusBriefCard] getStatusBrief failed:', err);
      setUnavailable({ reason: 'data_unavailable' });
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  useEffect(() => {
    if (!locked) fetchBrief();
  }, [locked]);

  if (locked) return <LockedAiStatusBriefCard variant={variant} />;

  if (initialized && !brief && !loading && !unavailable) return null;

  return (
    <div
      className={`card${variant === 'v3' ? ' h-100' : ' mb-6'}`}
      style={{ borderLeft: '3px solid #009EF7', ...(variant === 'v3' ? { minHeight: V3_MIN_HEIGHT } : {}) }}
    >
      <div
        className={`card-header border-0 py-3${isCollapsible ? ' cursor-pointer' : ''}`}
        onClick={isCollapsible ? () => setCollapsed(c => !c) : undefined}
      >
        <div className='d-flex align-items-center gap-2'>
          <span
            className='w-30px h-30px rounded-circle d-flex align-items-center justify-content-center'
            style={{ background: 'linear-gradient(135deg, #009EF7 0%, #6B7CF8 100%)' }}
          >
            <KTIcon iconName='message-text-2' className='text-white fs-6' />
          </span>
          <span className='fw-bold text-gray-800 fs-6'>{'AI Business Summary'.toTranslatedDisplay()}</span>
          <span className='badge badge-light-primary ms-1' style={{ fontSize: '10px' }}>{'AI'.toTranslatedDisplay()}</span>
        </div>
        <div className='d-flex align-items-center gap-3'>
          {loading && (
            <span className='spinner-border spinner-border-sm text-primary' style={{ width: '14px', height: '14px' }} />
          )}
          {isCollapsible && (
            <KTIcon iconName={effectiveCollapsed ? 'arrow-down' : 'arrow-up'} className='fs-5 text-gray-500' />
          )}
        </div>
      </div>

      {!effectiveCollapsed && (
        <div className='card-body pt-0 pb-4 px-6'>
          {loading && !brief && (
            <div className='d-flex align-items-center gap-2 text-muted fs-7'>
              <span className='spinner-border spinner-border-sm' style={{ width: '14px', height: '14px' }} />
              {'Analysing your financials…'.toTranslatedDisplay()}
            </div>
          )}
          {brief && variant === 'v3' && (
            <>
              <p className='text-gray-900 fw-bold fs-7 mb-3' style={{ lineHeight: '1.5' }}>{brief.summary}</p>
              <div className='row g-4'>
                {brief.positives.length > 0 && (
                  <div className='col-md-6'>
                    <div className='fw-bold text-success fs-9 text-uppercase mb-2' style={{ letterSpacing: '.06em' }}>{'Going well'.toTranslatedDisplay()}</div>
                    <ul className='list-unstyled mb-0'>
                      {brief.positives.map((item, i) => (
                        <li key={i} className='d-flex align-items-start gap-2 mb-1'>
                          <i className='bi bi-check-circle-fill text-success fs-8 mt-1' />
                          <span className='text-gray-700 fs-8' style={{ lineHeight: '1.45' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {brief.improvements.length > 0 && (
                  <div className='col-md-6'>
                    <div className='fw-bold text-warning fs-9 text-uppercase mb-2' style={{ letterSpacing: '.06em' }}>{'Do this next'.toTranslatedDisplay()}</div>
                    <ul className='list-unstyled mb-0'>
                      {brief.improvements.map((item, i) => (
                        <li key={i} className='d-flex align-items-start gap-2 mb-1'>
                          <span
                            className='d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle bg-light-warning text-warning fw-bolder'
                            style={{ width: 16, height: 16, fontSize: '9px', marginTop: '2px' }}
                          >
                            {i + 1}
                          </span>
                          <span className='text-gray-700 fs-8' style={{ lineHeight: '1.45' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}
          {brief && variant === 'default' && (
            <>
              <p className='text-gray-800 fw-semibold fs-7 mb-4' style={{ lineHeight: '1.7' }}>{brief.summary}</p>
              <div className='row g-5'>
                {brief.positives.length > 0 && (
                  <div className='col-md-6'>
                    <div className='fw-bold text-success fs-8 text-uppercase mb-2'>{'Going well'.toTranslatedDisplay()}</div>
                    <ul className='list-unstyled mb-0'>
                      {brief.positives.map((item, i) => (
                        <li key={i} className='d-flex align-items-start gap-2 mb-2'>
                          <i className='bi bi-check-circle-fill text-success fs-7 mt-1' />
                          <span className='text-gray-700 fs-7' style={{ lineHeight: '1.6' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {brief.improvements.length > 0 && (
                  <div className='col-md-6'>
                    <div className='fw-bold text-warning fs-8 text-uppercase mb-2'>{'Areas to improve'.toTranslatedDisplay()}</div>
                    <ul className='list-unstyled mb-0'>
                      {brief.improvements.map((item, i) => (
                        <li key={i} className='d-flex align-items-start gap-2 mb-2'>
                          <i className='bi bi-arrow-up-right-circle-fill text-warning fs-7 mt-1' />
                          <span className='text-gray-700 fs-7' style={{ lineHeight: '1.6' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </>
          )}
          {!loading && !brief && unavailable?.reason === 'insufficient_data' && (
            <p className='text-muted fs-7 mb-0'>
              {`${'Not enough data yet to generate an AI summary'.toTranslatedDisplay()} (${unavailable.invoiceCount ?? 0}/${unavailable.requiredInvoiceCount ?? 5} ${'invoices this month'.toTranslatedDisplay()}).`}
            </p>
          )}
          {!loading && !brief && unavailable?.reason === 'data_unavailable' && (
            <p className='text-muted fs-7 mb-0'>
              {'Could not generate an AI summary right now. Please try again later.'.toTranslatedDisplay()}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AiStatusBriefCard;
